/**
 * Volume Tier Component — Atelier Compatible
 * Uses manual final prices per tier instead of percentage discounts.
 */
(function () {
  'use strict';

  if (customElements.get('volume-tier-component')) return;

  class VolumeTierComponent extends HTMLElement {
    constructor() {
      super();
      this._onTierClick = this._onTierClick.bind(this);
      this._onTierKeydown = this._onTierKeydown.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._activeIndex = -1;
    }

    connectedCallback() {
      this._tiers = this._parseTiers();
      this._cards = Array.from(this.querySelectorAll('.vtier-card'));
      this._moneyFormat =
        this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';

      this._cards.forEach(function (card) {
        card.addEventListener('click', this._onTierClick);
        card.addEventListener('keydown', this._onTierKeydown);
      }, this);

      document.addEventListener('variant:change', this._onVariantChange);
      document.addEventListener('variantChange', this._onVariantChange);

      this._formComponent = this._findProductForm();
      if (this._formComponent) {
        var idInput = this._formComponent.querySelector('input[name="id"]');
        if (idInput) {
          idInput.addEventListener('change', this._onVariantChange);
          this._variantInput = idInput;
        }
      }

      var self = this;
      requestAnimationFrame(function () {
        self._selectDefaultTier();
        self._syncCurrentVariantPrices();
      });
    }

    disconnectedCallback() {
      this._cards.forEach(function (card) {
        card.removeEventListener('click', this._onTierClick);
        card.removeEventListener('keydown', this._onTierKeydown);
      }, this);

      document.removeEventListener('variant:change', this._onVariantChange);
      document.removeEventListener('variantChange', this._onVariantChange);

      if (this._variantInput) {
        this._variantInput.removeEventListener('change', this._onVariantChange);
      }
    }

    _parseTiers() {
      try {
        return JSON.parse(this.dataset.tiers || '[]');
      } catch (e) {
        console.error('[VolumeTier] Invalid tier JSON', e);
        return [];
      }
    }

    _selectDefaultTier() {
      var defaultIndex = 1;
      this._tiers.forEach(function (tier, i) {
        if (tier['default']) defaultIndex = i;
      });
      this._selectTier(defaultIndex);
    }

    _selectTier(index) {
      if (index < 0 || index >= this._cards.length) return;

      var card = this._cards[index];
      if (card.dataset.unavailable === 'true') return;

      this._cards.forEach(function (c, i) {
        var selected = i === index;
        c.setAttribute('aria-checked', selected ? 'true' : 'false');
        c.setAttribute('tabindex', selected ? '0' : '-1');
      });

      var qty = parseInt(card.dataset.quantity, 10) || 1;
      this._setQuantity(qty);
      this._activeIndex = index;
    }

    _onTierClick(e) {
      var card = e.target.closest('.vtier-card');
      if (!card) return;
      var index = parseInt(card.dataset.tierIndex, 10);
      this._selectTier(index);
      card.focus();
    }

    _onTierKeydown(e) {
      var key = e.key;
      var index = this._activeIndex;
      var len = this._cards.length;

      if (key === 'ArrowDown' || key === 'ArrowRight') {
        e.preventDefault();
        index = Math.min(index + 1, len - 1);
        while (index < len && this._cards[index].dataset.unavailable === 'true') {
          index++;
        }
        if (index < len) {
          this._selectTier(index);
          this._cards[index].focus();
        }
      } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
        e.preventDefault();
        index = Math.max(index - 1, 0);
        while (index >= 0 && this._cards[index].dataset.unavailable === 'true') {
          index--;
        }
        if (index >= 0) {
          this._selectTier(index);
          this._cards[index].focus();
        }
      } else if (key === ' ' || key === 'Enter') {
        e.preventDefault();
        var card = e.target.closest('.vtier-card');
        if (card) {
          this._selectTier(parseInt(card.dataset.tierIndex, 10));
        }
      }
    }

    _onVariantChange(e) {
      var variantPrice = null;
      var variantComparePrice = null;
      var variantAvailable = true;

      if (e && e.detail) {
        var v = e.detail.variant || e.detail;
        if (v.price != null) variantPrice = v.price;
        if (v.compare_at_price != null) variantComparePrice = v.compare_at_price;
        if (v.available != null) variantAvailable = v.available;
      }

      if (variantPrice == null || variantComparePrice == null) {
        var variantData = this._getVariantDataFromDOM();
        if (variantData) {
          if (variantPrice == null && variantData.price != null) {
            variantPrice = variantData.price;
          }
          if (variantComparePrice == null && variantData.compare_at_price != null) {
            variantComparePrice = variantData.compare_at_price;
          }
          if (variantData.available != null) {
            variantAvailable = variantData.available;
          }
        }
      }

      if (variantPrice != null) {
        this.dataset.basePrice = String(variantPrice);
      }

      if (variantComparePrice != null && variantComparePrice > 0) {
        this.dataset.comparePrice = String(variantComparePrice);
      } else if (variantPrice != null) {
        this.dataset.comparePrice = String(variantPrice);
      }

      this._updatePrices(parseInt(this.dataset.basePrice, 10) || 0);

      this.dataset.available = variantAvailable ? 'true' : 'false';
      this._updateAvailability(variantAvailable);
    }

    _syncCurrentVariantPrices() {
      var variantData = this._getVariantDataFromDOM();
      var basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      var comparePrice = parseInt(this.dataset.comparePrice, 10) || 0;

      if (variantData) {
        if (variantData.price != null) {
          basePrice = variantData.price;
          this.dataset.basePrice = String(basePrice);
        }

        if (variantData.compare_at_price != null && variantData.compare_at_price > 0) {
          comparePrice = variantData.compare_at_price;
          this.dataset.comparePrice = String(comparePrice);
        } else {
          comparePrice = basePrice;
          this.dataset.comparePrice = String(comparePrice);
        }

        if (variantData.available != null) {
          this.dataset.available = variantData.available ? 'true' : 'false';
          this._updateAvailability(variantData.available);
        }
      } else {
        if (!comparePrice || comparePrice <= 0) {
          comparePrice = basePrice;
          this.dataset.comparePrice = String(comparePrice);
        }
      }

      this._updatePrices(basePrice);
    }

    _findProductForm() {
      var parent = this.closest('.buy-buttons-block') || this.parentElement;

      if (parent) {
        var form = parent.querySelector('product-form-component');
        if (form) {
          this._attachDebugListeners(form);
          return form;
        }
      }

      var sibling = this.nextElementSibling;
      while (sibling) {
        if (
          sibling.tagName &&
          sibling.tagName.toLowerCase() === 'product-form-component'
        ) {
          this._attachDebugListeners(sibling);
          return sibling;
        }
        sibling = sibling.nextElementSibling;
      }

      var section = this.closest('.shopify-section');
      if (section) {
        return section.querySelector('product-form-component');
      }

      return null;
    }

    _attachDebugListeners(formComponent) {
      var form =
        formComponent.tagName === 'FORM'
          ? formComponent
          : formComponent.querySelector('form');

      if (form) {
        var btn = form.querySelector('button[type="submit"], button[name="add"]');
        if (btn) {
          btn.addEventListener('click', function () {
            if (btn.type === 'button') {
              form.requestSubmit();
            }
          });
        }
      }
    }

    _setQuantity(qty) {
      if (!this._formComponent) {
        this._formComponent = this._findProductForm();
      }
      if (!this._formComponent) return;

      var el = this._formComponent.querySelector('input[name="quantity"]');

      if (!el) {
        this._formComponent.dataset.quantityDefault = qty.toString();

        var form = this._formComponent.querySelector('form');
        if (form) {
          var existing = form.querySelector('input[name="quantity"]');
          if (!existing) {
            var hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'quantity';
            hidden.value = qty;
            hidden.setAttribute('data-volume-tier-injected', 'true');
            form.appendChild(hidden);
          } else {
            existing.value = qty;
          }
        }
        return;
      }

      var min = parseInt(el.getAttribute('min'), 10) || 1;
      var max = parseInt(el.getAttribute('max'), 10) || Infinity;
      var step = parseInt(el.getAttribute('step'), 10) || 1;
      var clamped = Math.max(min, Math.min(max, qty));
      clamped = Math.round(clamped / step) * step;

      var nativeSet = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value'
      ).set;

      nativeSet.call(el, clamped);
      el.setAttribute('value', clamped);

      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
    }

    _updatePrices(basePrice) {
      var self = this;

      this._cards.forEach(function (card, i) {
        var tier = self._tiers[i];
        if (!tier) return;

        var qty = parseInt(tier.quantity, 10) || 1;
        var comparePrice = parseInt(self.dataset.comparePrice, 10);

        if (isNaN(comparePrice) || comparePrice <= 0) {
          comparePrice = basePrice;
        }

        var total = comparePrice * qty;
        var finalPrice = parseInt(tier.finalPrice, 10);

        if (isNaN(finalPrice)) {
          finalPrice = basePrice * qty;
        }

        var finalEl = card.querySelector('[data-role="final-price"]');
        var compareEl = card.querySelector('[data-role="compare-price"]');
        var pillEl = card.querySelector('[data-role="tier-pill"]');

        if (finalEl) {
          finalEl.textContent = self._formatMoney(finalPrice);
        }

        if (pillEl) {
          var saving = total - finalPrice;
          if (saving > 0 && pillEl.textContent.trim() === '') {
            pillEl.textContent = 'Ahorras ' + self._formatMoney(saving);
          }
        }

        if (compareEl) {
          if (finalPrice < total) {
            compareEl.textContent = self._formatMoney(total);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        } else if (finalPrice < total) {
          var pricesWrap = card.querySelector('.vtier-prices');
          if (pricesWrap) {
            var newCompare = document.createElement('div');
            newCompare.className = 'vtier-price-original';
            newCompare.setAttribute('data-role', 'compare-price');
            newCompare.textContent = self._formatMoney(total);
            pricesWrap.appendChild(newCompare);
          }
        }
      });
    }

    _updateAvailability(available) {
      this._cards.forEach(function (card) {
        card.dataset.unavailable = available ? 'false' : 'true';
      });
    }

    _formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      var fmt = this._moneyFormat;
      var amountComma = amount.replace('.', ',');
      var amountNoDecimals = Math.round(cents / 100).toString();

      return fmt
        .replace('{{amount_with_comma_separator}}', amountComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
        .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"))
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);
    }

    _getVariantDataFromDOM() {
      var section = this.closest('.shopify-section');
      var scripts = (section || document).querySelectorAll(
        'script[type="application/json"]'
      );

      var variantId = null;
      if (this._variantInput) {
        variantId = parseInt(this._variantInput.value, 10);
      }

      for (var i = 0; i < scripts.length; i++) {
        try {
          var data = JSON.parse(scripts[i].textContent);
          var product = data.product || data;
          if (product && product.variants) {
            for (var j = 0; j < product.variants.length; j++) {
              if (product.variants[j].id === variantId) {
                return product.variants[j];
              }
            }
          }
        } catch (e) {}
      }

      return null;
    }
  }

  customElements.define('volume-tier-component', VolumeTierComponent);
})();